import { useCallback, useEffect, useRef } from "preact/hooks";
import { recordLive } from "./perf.ts";

/**
 * Live updates. Preferred transport: one WebSocket per page to the LiveHub
 * Durable Object (/api/live), which pushes the change counter the moment any
 * device writes. If the socket can't be opened, the page falls back to the
 * /api/events long-poll until the socket works again. Either way, subscribers
 * are told "something changed" and refetch. Everything pauses in the background.
 */

/** Why a listener is being notified: a real change, the tab regaining focus, or a poll error retry. */
type NotifyReason = "change" | "visible" | "error";
type Listener = (reason: NotifyReason) => void;
const listeners = new Set<Listener>();
let version = 0;

let socket: WebSocket | null = null;
let pollAbort: AbortController | null = null;
let running = false;
let backoffMs = 1000;
let socketFailures = 0;
let pingTimer: number | undefined;

/** WS drops and poll-fallback starts since the last takeLiveCounters() call; feeds perf.session. */
let reconnects = 0;
let fallbacks = 0;

const PING_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;
const FALLBACK_AFTER_FAILURES = 2;
const RETRY_SOCKET_AFTER_MS = 60_000;

function notify(reason: NotifyReason): void {
  for (const l of [...listeners]) l(reason);
}

function applyVersion(next: number): void {
  if (next !== version) {
    version = next;
    notify("change");
  }
}

/** Reads and resets the WS reconnect / poll-fallback counters. Called once per perf.session report. */
export function takeLiveCounters(): { reconnects: number; fallbacks: number } {
  const out = { reconnects, fallbacks };
  reconnects = 0;
  fallbacks = 0;
  return out;
}

function visible(): boolean {
  return document.visibilityState === "visible";
}

// ---------- WebSocket transport ----------

function openSocket(): void {
  if (socket || !running || !visible()) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/api/live`);
  socket = ws;
  let opened = false;

  ws.onopen = () => {
    opened = true;
    socketFailures = 0;
    backoffMs = 1000;
    stopPolling();
    pingTimer = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send("ping"), PING_MS);
  };
  ws.onmessage = (e) => {
    if (typeof e.data !== "string" || e.data === "pong") return;
    try {
      const msg = JSON.parse(e.data) as { type: string; version: number };
      if (msg.type === "hello" || msg.type === "change") applyVersion(msg.version);
    } catch {
      /* ignore */
    }
  };
  ws.onclose = () => {
    window.clearInterval(pingTimer);
    if (socket === ws) socket = null;
    if (!running) return;
    if (opened) reconnects++; // an established socket dropped
    if (!opened) socketFailures++;
    if (socketFailures >= FALLBACK_AFTER_FAILURES) {
      startPolling();
      window.setTimeout(() => {
        socketFailures = 0;
        if (running && visible()) openSocket();
      }, RETRY_SOCKET_AFTER_MS);
      return;
    }
    window.setTimeout(() => running && visible() && openSocket(), backoffMs);
    backoffMs = Math.min(MAX_BACKOFF_MS, backoffMs * 2);
  };
  ws.onerror = () => {
    /* onclose follows */
  };
}

function closeSocket(): void {
  window.clearInterval(pingTimer);
  const ws = socket;
  socket = null;
  ws?.close();
}

// ---------- long-poll fallback ----------

async function pollLoop(controller: AbortController): Promise<void> {
  while (running && pollAbort === controller && visible()) {
    try {
      const res = await fetch(`/api/events?since=${version}`, { credentials: "same-origin", signal: controller.signal });
      if (res.status === 401) return;
      if (!res.ok) throw new Error(String(res.status));
      applyVersion(((await res.json()) as { version: number }).version);
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      await new Promise((r) => setTimeout(r, 5000));
      notify("error");
    }
  }
}

function startPolling(): void {
  if (pollAbort) return;
  fallbacks++;
  pollAbort = new AbortController();
  void pollLoop(pollAbort);
}

function stopPolling(): void {
  pollAbort?.abort();
  pollAbort = null;
}

// ---------- lifecycle ----------

function onVisibility(): void {
  if (visible()) {
    notify("visible"); // we may have missed changes while hidden
    openSocket();
  } else {
    closeSocket();
    stopPolling();
  }
}

function start(): void {
  if (running) return;
  running = true;
  document.addEventListener("visibilitychange", onVisibility);
  openSocket();
}

function stop(): void {
  running = false;
  document.removeEventListener("visibilitychange", onVisibility);
  closeSocket();
  stopPolling();
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  start();
  return () => {
    listeners.delete(l);
    if (listeners.size === 0) stop();
  };
}

/**
 * Hears only real changes the server reports (not the tab coming back or a poll retry): the header
 * peon's moods time an hour of quiet from the last one (shared/peon-mood.ts). Returns the unsubscribe.
 */
export function onLiveChange(fn: () => void): () => void {
  return subscribe((reason) => {
    if (reason === "change") fn();
  });
}

/** Transport currently in use, for diagnostics. */
export function liveTransport(): "socket" | "poll" | "none" {
  if (socket?.readyState === WebSocket.OPEN) return "socket";
  if (pollAbort) return "poll";
  return "none";
}

/**
 * Runs `fn(true)` on mount, then `fn(false)` every time the server reports a
 * change (or the app returns to the foreground). Re-arms when `deps` change.
 * Returns a function that runs `fn(true)` again, for a "Try again" after a
 * failed first load.
 */
export function useLive(fn: (initial: boolean) => void | Promise<void>, deps: unknown[]): () => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    void fnRef.current(true);
    return subscribe((reason) => {
      if (reason !== "change") {
        void fnRef.current(false);
        return;
      }
      // Only a real change is timed: refetches triggered by regaining focus or a poll retry
      // aren't "how long did the live path take to settle".
      void (async () => {
        const start = performance.now();
        try {
          await fnRef.current(false);
        } finally {
          recordLive(performance.now() - start);
        }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return useCallback(() => void fnRef.current(true), []);
}
